import { MDBCol } from "mdb-react-ui-kit";
import React, {
    FC,
    HTMLInputTypeAttribute,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import styles from "@/styles/common/Input.module.scss";
import Select from "react-select";
import { ColumnProps } from "../../typings/mdb-react-ui-kit";
import mediaControlSvg from "../../utils/svg-icon/mediaControl";
import userInterfaceSvg from "../../utils/svg-icon/userInterface";

export type TInputStatus = "error" | "success" | "default";

export type TInpuntValue = string | number | readonly string[] | undefined;

export interface IInputProps {
    maxLength?: number;
    label?: string;
    placeholder?: string;
    name: string;
    id?: string;
    type?: HTMLInputTypeAttribute | "select" | "multi-select";
    value: TInpuntValue;
    hideValueAttribute?: boolean;
    required?: boolean;
    columnProps?: ColumnProps;
    options?: { value: string; label: string }[];
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onChangeSelect?: (value: React.ChangeEvent<HTMLSelectElement>) => void;
    onDelete?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
    inputStatus?: TInputStatus;
    diasbleStatusIcon?: boolean;
    statusDefault?: boolean;
    validationCb?: (value: TInpuntValue) => boolean;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    inputSelectProps?: React.InputHTMLAttributes<HTMLSelectElement>;
    disabled?: boolean;
    flagDefaultStatus?: boolean;
    hideBorder?: boolean;
    inputClassName?: string;
    isSelectConfigurator?: boolean;
    onFocus?: () => void;
    style?: React.CSSProperties;
    styleContainer?: React.CSSProperties;
}

type TInpuntStatus = {
    className: string;
    icon: ReactNode;
    status: TInputStatus;
};

const { CheckLine, ArrowDownSimpleLine, EyeLine } = userInterfaceSvg;

const getIcon = (
    type: HTMLInputTypeAttribute | "select",
    validation: boolean
) => {
    const { CloseCircleFilled } = mediaControlSvg;

    if (type === "password") {
        return (
            <div className={`${styles.icon}`}>
                <EyeLine />
            </div>
        );
    }

    if (type === "select") {
        return (
            <div className={`${styles.icon} ${styles.iconSelect}`}>
                <ArrowDownSimpleLine />
            </div>
        );
    }
    if (validation) {
        return (
            <div className={`${styles.icon} ${styles.iconSuccess}`}>
                <CheckLine />
            </div>
        );
    }
    return (
        <div className={`${styles.icon} ${styles.iconError}`}>
            <CloseCircleFilled />
        </div>
    );
};

const Input: FC<IInputProps> = ({
    maxLength,
    label,
    id,
    type,
    name,
    value,
    hideValueAttribute,
    placeholder,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onChange = () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onBlur = () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onChangeSelect = () => {},
    options = [],
    onDelete,
    validationCb,
    columnProps = { size: 12 },
    diasbleStatusIcon,
    required,
    inputProps = {},
    inputSelectProps = {},
    statusDefault,
    disabled,
    flagDefaultStatus,
    hideBorder,
    inputClassName = "",
    isSelectConfigurator,
    onFocus,
    styleContainer = {},
}) => {
    const [typeInput, setTypeInput] = useState<
        HTMLInputTypeAttribute | "select"
    >(type || "text");
    const [focused, setFocused] = useState<boolean>(false);
    const [isMultiSelectTouched, setIsMultiSelectTouched] =
        useState<boolean>(false);
    const [inputStatus, setInputStatus] = useState<TInpuntStatus>({
        className: "",
        icon: "",
        status: "default",
    });
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    const multiSelectRef = useRef<any>();

    const isSelect = type === "select";
    const isPassword = type === "password";
    const isMultiSelect = type === "multi-select";

    const multiSelectBorder = (
        isFocused: boolean,
        isTouched: boolean,
        valueArray: any[]
    ) => {
        if (isFocused) return "0 0 0 2px #55ce97";
        if (isTouched && (valueArray ?? []).length <= 0)
            return "0 0 0 2px #ed6f6f";
        if ((valueArray ?? []).length > 0) return "0 0 0 2px #55ce97";
        return "0 0 0 2px #e6e8ec";
    };

    const customMultiSelectStyles = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        control: (provided: any, state: any) => ({
            ...provided,
            borderRadius: "12px",
            border: "unset",
            // eslint-disable-next-line sonarjs/no-all-duplicated-branches
            borderColor: state.isFocused ? "#55ce97" : "#e6e8ec",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            // eslint-disable-next-line sonarjs/no-all-duplicated-branches
            boxShadow: multiSelectBorder(
                state.isFocused,
                isMultiSelectTouched,
                value as any[]
            ),
            outline: "none",
        }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        option: (provided: any, state: any) => ({
            ...provided,
            // eslint-disable-next-line sonarjs/no-all-duplicated-branches
            backgroundColor: state.isSelected ? "transparent" : "transparent",
            // eslint-disable-next-line sonarjs/no-all-duplicated-branches
            color: state.isSelected ? "white" : "black",
            ":active": {
                backgroundColor: "#e6e8ec",
            },
        }),
    };

    const customDropdownIndicator = (input: any) => {
        return (
            <div className="customDropdownIndicator">
                {input.getValue().length > 0 ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="#55ce97"
                    >
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                ) : (
                    <ArrowDownSimpleLine />
                )}
            </div>
        );
    };

    useEffect(() => {
        if (
            isMultiSelect &&
            Array.isArray(value) &&
            value.length === 0 &&
            multiSelectRef.current?.state.selectValue.length > 0
        ) {
            multiSelectRef?.current?.clearValue();
        }
    }, [value]);

    const defaultValidationCb = (val: TInpuntValue) =>
        !(required && !(val as string)?.trim());

    const getValidationSatusData = (validation: boolean): TInpuntStatus => ({
        className: validation ? styles.inputSuccess : styles.inputError,
        icon: getIcon(type || "select", validation),
        status: validation ? "success" : "error",
    });

    const getInputStatusData = (): TInpuntStatus => {
        if (!focused) {
            return {
                className: "",
                icon: isPassword
                    ? getIcon(type, false)
                    : isSelectConfigurator
                    ? getIcon("select", false)
                    : "",
                status: "default",
            };
        }
        if (flagDefaultStatus) {
            return { className: "", icon: "", status: "default" };
        }
        if (disabled || statusDefault) {
            return { className: "", icon: "", status: "default" };
        }
        if (validationCb) {
            return getValidationSatusData(validationCb(value));
        }
        if (!validationCb && required) {
            return getValidationSatusData(defaultValidationCb(value));
        }
        if (!required) {
            return { className: "", icon: "", status: "default" };
        }
        return { className: "", icon: "", status: "default" };
    };

    const valueAttribute = hideValueAttribute ? {} : { value };

    // Usa useMemo per calcolare lo status
    const newInputStatus = React.useMemo(
        () => getInputStatusData(),
        [
            focused,
            flagDefaultStatus,
            disabled,
            statusDefault,
            validationCb,
            value,
            required,
            type,
            isPassword,
            isSelectConfigurator,
        ]
    );

    // Usa useRef per tracciare l'ultimo status e evitare aggiornamenti inutili
    const lastStatusRef = React.useRef<string>("");

    useEffect(() => {
        // Crea una chiave univoca per lo status
        const statusKey = `${newInputStatus.status}|${newInputStatus.className}`;

        // Se lo status non è cambiato, non aggiornare
        if (lastStatusRef.current === statusKey) {
            return;
        }

        // Aggiorna solo se lo status è realmente cambiato
        setInputStatus(newInputStatus);
        lastStatusRef.current = statusKey;
    }, [newInputStatus]);

    return (
        <MDBCol
            {...columnProps}
            className={`${styles.container} ${columnProps.className || ""}`}
        >
            {label && (
                <p className="w-100">
                    {label}
                    {required && "*"}
                </p>
            )}
            <div className="position-relative" style={styleContainer}>
                {!diasbleStatusIcon && (
                    <span
                        onClick={e => {
                            if (type === "password") {
                                setTypeInput(
                                    typeInput === "password"
                                        ? "text"
                                        : "password"
                                );
                                return;
                            }
                            if (inputStatus.status === "error" && onDelete) {
                                onDelete(e);
                            }
                        }}
                        className={onDelete || isPassword ? "pointer" : ""}
                    >
                        {inputStatus.icon}
                    </span>
                )}
                {!isSelect && !isMultiSelect && (
                    <input
                        type={typeInput}
                        name={name}
                        maxLength={maxLength}
                        onFocus={() => {
                            setFocused(true); // Mantiene la logica interna
                            if (onFocus) {
                                // Esegue la nostra funzione se è stata passata
                                onFocus();
                            }
                        }}
                        placeholder={placeholder || ""}
                        className={`${styles.input} w-100 ${
                            inputStatus.className
                        } ${hideBorder ? "border-0" : ""} ${inputClassName}`}
                        style={{
                            paddingRight: "40px",
                        }}
                        id={id || name}
                        required
                        onChange={onChange}
                        disabled={disabled}
                        onBlur={onBlur}
                        {...inputProps}
                        {...valueAttribute}
                    />
                )}
                {isSelect && (
                    <select
                        name={name}
                        className={`${styles.input} w-100 ${inputStatus.className} pointer ${inputClassName}`}
                        onFocus={() => setFocused(true)}
                        id={id || name}
                        required
                        value={value}
                        onChange={onChangeSelect as never}
                        {...inputSelectProps}
                        {...valueAttribute}
                    >
                        {options.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                )}
                {isMultiSelect && (
                    <Select
                        onBlur={() => setIsMultiSelectTouched(true)}
                        styles={customMultiSelectStyles}
                        ref={multiSelectRef}
                        onChange={onChangeSelect as never}
                        options={options}
                        isMulti
                        components={{
                            DropdownIndicator: customDropdownIndicator, // Personalizza la freccetta
                        }}
                    />
                )}
            </div>
        </MDBCol>
    );
};

export default Input;
